// Calcul de la revue opérationnelle : de la lecture brute de Flex au classement des chantiers.
// Logique reprise à l'identique de la maquette « Pilotage chantiers » ; pure, sans accès à SYNC.

import { addDays, daysBetween } from '../format.js';
import type {
  OpsAnomaly,
  OpsChantier,
  OpsClass,
  OpsDataset,
  OpsEtat,
  OpsInterne,
  OpsProj,
  OpsN1Order,
  OpsRaw,
  Poste,
  Postes,
} from './types.js';

/** Seuils du classement. */
export const OPS_PRM = {
  /** Au-delà de ce facturé (%), le chantier est « en fin de chantier » : la marge est quasi figée. */
  pil: 90,
  /** Recul de marge (points) à partir duquel un chantier est en dérive. */
  der: 3,
  /** Recul de marge (points) à partir duquel un chantier est à surveiller. */
  rec: 1,
  /** Retard de facturation (points d'avancement sur le facturé). */
  bill: 10,
  /** Horizon des démarrages, en jours. */
  hz: 60,
};
export type OpsParams = typeof OPS_PRM;

const GROUP =
  /^(ODICE|SAVEC$|MARTINON|PROVENCE FROID$|DIMCO( FRANCE)?$|FC33$|FROID CUISINE 33$|SVR EQUIPEMENT$|ALTECC|ITR - MIDITECH|BLOQUE-MIDITECH|NPU - ALTECC|GROUPE ODICE)/i;
const OWN: Record<string, RegExp> = {
  ALTECC: /ALTECC/i,
  DIMCO: /^DIMCO$/i,
  'DIMCO-FR': /^DIMCO FRANCE/i,
  FC33: /^(FC33|FROID CUISINE 33)/i,
  MARTINON: /^MARTINON/i,
  MIDITECH: /MIDITECH/i,
  PF: /^PROVENCE FROID/i,
  SAVEC: /^SAVEC/i,
  SVREQUIPEM: /^SVR EQUIPEMENT/i,
  ODICEENT: /^ODICE/i,
};

const N = (v: unknown): number => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};
const S = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s === '' || s.toLowerCase() === 'null' ? null : s;
};

// --- Formats de texte (montants toujours en k€, comme dans la maquette) ---

const nf0 = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });
/** Montant en k€ : 12,4 k€, 85 k€, −3,2 k€. */
export function eurK(v: number): string {
  v = Number(v) || 0;
  const a = Math.abs(v);
  const k = a / 1e3;
  const s = k >= 10 || k === 0 ? nf0.format(Math.round(k)) : k.toFixed(1).replace('.', ',');
  return `${v < 0 && s !== '0' && s !== '0,0' ? '−' : ''}${s} k€`;
}
export const eurKSigned = (v: number): string => (v > 0 ? '+' : '') + eurK(v);
/** Montant complet : 12 345 €. */
export const fullEur = (v: number): string => `${v < 0 ? '−' : ''}${nf0.format(Math.abs(Math.round(v)))} €`;
export const fullEurSigned = (v: number): string => (v > 0 ? '+' : '') + fullEur(v);
/** Pourcentage à une décimale ; null et valeurs infinies donnent un tiret. */
export const pc1 = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(v) ? '—' : `${String(Math.round(v * 10) / 10).replace('.', ',')} %`;
/** Même chose, avec le signe moins typographique. */
export const pcm = (v: number | null | undefined): string => pc1(v).replace('-', '−');
/** Écart en points : +1,2 pt. */
export const ptxt = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(v)
    ? '—'
    : `${v > 0 ? '+' : v < 0 ? '−' : ''}${String(Math.abs(Math.round(v * 10) / 10)).replace('.', ',')} pt`;

const frl = (s: string | null | undefined): string => {
  if (!s) return '—';
  const [y, m, d] = s.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};
const fr = (s: string | null | undefined, asOf: string): string => {
  if (!s) return '';
  const [y, m, d] = s.slice(0, 10).split('-');
  return `${d}/${m}${y !== asOf.slice(0, 4) ? `/${(y ?? '').slice(2)}` : ''}`;
};

export const ymAdd = (ym: string, n: number): string => {
  const [y0, m0] = ym.split('-').map(Number) as [number, number];
  const t = y0 * 12 + (m0 - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
};

// --- Postes ---

export const zeroPostes = (): Postes => ({ ca: 0, ach: 0, st: 0, mo: 0, oth: 0 });
export const POSTES: readonly Poste[] = ['ca', 'ach', 'st', 'mo', 'oth'];
export const addPostes = (a: Postes, b: Postes): Postes => {
  for (const p of POSTES) a[p] += b[p] || 0;
  return a;
};
export const costOf = (x: Postes): number => x.ach + x.st + x.mo + x.oth;
export const margOf = (x: Postes): number => x.ca - costOf(x);
/** Taux de marge en %, null sans CA. */
export const rateOf = (x: Postes): number | null => (x.ca ? (margOf(x) / x.ca) * 100 : null);

// --- Projection du reste à faire ---

function proj(
  r: { raf: number; RC: number; fin: string | null; est: string | null },
  V: Record<string, number[]> | undefined,
  asOf: string,
): OpsProj {
  const next = asOf.slice(0, 7);
  const out: Record<string, { f: number; c: number }> = {};
  const sd = { f: 0, c: 0 };
  let method = '';
  const add = (m: string, f: number, c: number) => {
    const o = (out[m] ??= { f: 0, c: 0 });
    o.f += f;
    o.c += c;
  };
  const target = r.fin && r.fin > asOf ? r.fin : r.est && r.est > asOf ? r.est : null;
  const span = (t: string) => {
    const ms: string[] = [];
    let m = next;
    const end = t.slice(0, 7) < next ? next : t.slice(0, 7);
    for (let i = 0; i < 120 && m <= end; i++) {
      ms.push(m);
      m = ymAdd(m, 1);
    }
    return ms;
  };
  const plan: [string, number][] = [];
  if (V)
    for (const y of Object.keys(V).sort())
      V[y]!.forEach((v, i) => {
        const m = `${y}-${String(i + 1).padStart(2, '0')}`;
        if (m >= next && v > 0) plan.push([m, v]);
      });
  const T = plan.reduce((a, p) => a + p[1], 0);
  if (T > 0) {
    // Le planning de facturation donne la facturation de chaque mois ; sa part dans le total
    // donne l'avancement du mois, appliqué au reste à dépenser.
    for (const [m, v] of plan) add(m, (r.raf * v) / T, (r.RC * v) / T);
    method = `ventilation de facturation Flex (${eurK(T)} sur ${plan.length} mois) appliquée au reste à facturer et aux coûts restants`;
  } else if (r.raf > 0 || r.RC > 0) {
    const sched: [string, number][] = [];
    if (target) {
      const ms = span(target);
      for (const m of ms) sched.push([m, r.raf / ms.length]);
      method = `pas de ventilation Flex : réparti à parts égales jusqu'à la fin ${r.fin && r.fin > asOf ? 'prévue' : 'estimée'} (${frl(target)})`;
    } else if (r.fin && r.fin <= asOf) {
      sched.push([next, r.raf]);
      method = 'pas de ventilation Flex, fin prévue dépassée : solde attendu ce mois-ci';
    } else {
      sd.f += r.raf;
      method = 'pas de ventilation Flex ni date de fin : reste sans date';
    }
    if (r.raf > 0) {
      for (const [m, f] of sched) add(m, f, (r.RC * f) / r.raf);
      sd.c += (r.RC * sd.f) / r.raf;
    } else {
      const ms = sched.length ? sched.map((x) => x[0]) : target ? span(target) : [next];
      for (const m of ms) add(m, 0, r.RC / ms.length);
    }
  }
  const months = Object.keys(out)
    .sort()
    .map((m) => ({ m, f: out[m]!.f, c: out[m]!.c }));
  return { months, sd, T, method: method || 'rien à projeter' };
}

// --- Classement ---

const DQ = new Set(['flexfact', 'flexach', 'flexst', 'nobudget', 'margeab', 'stale', 'overfact']);
const PLAB: Record<Poste, string> = {
  ca: 'CA revu en baisse',
  ach: 'Achats au-dessus du budget',
  st: 'Sous-traitance en dépassement',
  mo: 'Heures au-dessus du prévu',
  oth: 'Autres charges au-dessus du prévu',
};
const GLAB: Record<Poste, string> = {
  ca: 'CA revu en hausse',
  ach: 'Achats mieux négociés que prévu',
  st: 'Sous-traitance sous le budget',
  mo: 'Heures sous le prévu',
  oth: 'Autres charges sous le prévu',
};

/** Retard de facturation : avancement − facturé ≥ seuil, et au moins 1 k€ facturable. */
const billOf = (r: OpsChantier, prm: OpsParams) => {
  if (!(r.CAr > 0) || r.fa == null) return null;
  const g = r.cr - r.fa;
  if (g < prm.bill) return null;
  const e = Math.min(Math.round((r.cr / 100) * r.CAr - r.fact), r.raf);
  return e >= 1000 ? { g, e } : null;
};

/** Classe un chantier : en dérive, à surveiller ou bon, pilotable ou en fin de chantier. */
export function classify(r: OpsChantier, asOf: string, prm: OpsParams = OPS_PRM): OpsClass {
  const p = !(r.fa != null && r.fa >= prm.pil);
  const pv = rateOf(r.Pv);
  const tm = rateOf(r.Tm);
  const d = pv == null || tm == null ? 0 : tm - pv;
  const fd = !!(r.fin && r.fin < asOf && (r.raf >= 500 || r.RC >= 500));
  const late = fd && r.raf >= 500;
  const past = fd && !late;
  const bill = billOf(r, prm);
  const loss = r.Tm.ca > 0 && margOf(r.Tm) < 0;
  const nocond = !r.cond && !!r.deb && r.deb > asOf && r.deb <= addDays(asOf, 30);
  const an = r.anoms.filter((a) => DQ.has(a.k));
  const fi = an[0]?.txt ?? '';
  // Contribution de chaque poste : terminaison − prévu (positif = gain de marge).
  const ctr: [Poste, number][] = [
    ['ca', r.Tm.ca - r.Pv.ca],
    ['ach', -(r.Tm.ach - r.Pv.ach)],
    ['st', -(r.Tm.st - r.Pv.st)],
    ['mo', -(r.Tm.mo - r.Pv.mo)],
    ['oth', -(r.Tm.oth - r.Pv.oth)],
  ];
  const worst = [...ctr].sort((a, b) => a[1] - b[1])[0]!;
  const best = [...ctr].sort((a, b) => b[1] - a[1])[0]!;
  const issues: [string, string][] = [];
  if (bill)
    issues.push([
      'Facturation en retard',
      `Avancement ${r.cr} %, facturé ${r.fa} % : ${eurK(bill.e)} facturables`,
    ]);
  if (late)
    issues.push([
      'Fin dépassée, solde à facturer',
      `Fin prévue dépassée de ${daysBetween(r.fin!, asOf)} jours, ${eurK(r.raf)} restent à facturer`,
    ]);
  else if (past)
    issues.push([
      'Fin dépassée',
      `Fin prévue dépassée de ${daysBetween(r.fin!, asOf)} jours, ${eurK(r.RC)} restent à dépenser`,
    ]);
  if (nocond)
    issues.push([
      `Démarre dans ${daysBetween(asOf, r.deb!)} jours sans conducteur`,
      'Aucun conducteur affecté',
    ]);
  if (fi) issues.push(['Données à vérifier', fi]);
  if (d <= -prm.rec && d > -prm.der)
    issues.push(['Marge en léger recul', `Marge ${pcm(pv)} → ${pcm(tm)} (${ptxt(d)})`]);

  let e: OpsEtat;
  let reason: string;
  if (loss || (r.Pv.ca > 0 && d <= -prm.der)) {
    e = 'r';
    reason = loss ? 'Chantier à perte' : p ? PLAB[worst[0]] : 'Marge à terminaison sous le prévu';
  } else if (issues.length) {
    e = 'a';
    reason = issues[0]![0];
  } else {
    e = 'g';
    reason = !p
      ? r.fa! >= 100 && r.RC < 1
        ? 'Terminé, à clôturer'
        : r.fin && r.fin >= asOf
          ? `Réception prévue le ${frl(r.fin)}`
          : 'Fin de chantier conforme'
      : d >= 1
        ? GLAB[best[0]]
        : r.fact < 1 && r.cX < 1
          ? 'Non démarré, budget en place'
          : 'Conforme au budget';
  }

  // Pourquoi
  const why: string[] = [];
  const pct = (a: number, b: number) => (b > 0 ? Math.round(((a - b) / b) * 100) : null);
  if (loss) why.push(`Marge à terminaison négative : ${eurK(margOf(r.Tm))} (${pcm(tm)})`);
  if (e !== 'g') {
    const L: Record<Poste, [number, number, string]> = {
      ca: [r.Pv.ca, r.Tm.ca, 'CA revu'],
      ach: [r.Pv.ach, r.Tm.ach, 'Achats'],
      st: [r.Pv.st, r.Tm.st, 'Sous-traitance'],
      mo: [r.Pv.mo, r.Tm.mo, 'Main-d’œuvre'],
      oth: [r.Pv.oth, r.Tm.oth, 'Autres charges'],
    };
    ctr
      .filter((c) => c[1] <= -1000)
      .sort((a, b) => a[1] - b[1])
      .slice(0, 2)
      .forEach(([k, v]) => {
        const P = L[k];
        const q = pct(P[1], P[0]);
        why.push(
          k === 'ca'
            ? `CA revu ${eurKSigned(P[1] - P[0])} par rapport à la commande`
            : `${P[2]} à terminaison ${q != null ? `${q > 0 ? '+' : ''}${q} %` : eurKSigned(P[1] - P[0])} par rapport au chiffrage (${eurKSigned(-v)})`,
        );
      });
  }
  for (const i of issues) if (i[0] !== 'Données à vérifier' && !why.includes(i[1])) why.push(i[1]);
  if (e !== 'g' && p && r.RC > 0)
    why.push(`Avancement ${r.cr} % : ${100 - r.cr} % des coûts restent à dépenser (${eurK(r.RC)})`);
  if (e === 'g')
    why.push(d >= 1 ? `Marge à terminaison ${ptxt(d)} au-dessus du prévu` : 'Marge et planning conformes');

  // Ce qu'on peut encore faire, ou atterrissage
  const todo: OpsClass['todo'] = [];
  const gap = margOf(r.Pv) - margOf(r.Tm);
  if (p) {
    if (e === 'r') {
      if (worst[0] === 'ca' || gap > 0)
        todo.push(['Chiffrer un avenant ou une réclamation', gap > 0 ? gap : -margOf(r.Tm)]);
      const LAB: Record<string, string> = {
        ach: 'Renégocier les commandes restantes',
        st: 'Renégocier la sous-traitance restante',
        mo: 'Faire le point équipe sur les heures restantes',
        oth: 'Limiter les frais annexes (fournitures, port, prorata)',
      };
      for (const [k] of ctr.filter((c) => c[1] <= -1000 && c[0] !== 'ca'))
        if (r.Rf[k] >= 500) todo.push([LAB[k]!, r.Rf[k]]);
    }
    if (bill) todo.push(['Émettre une situation', bill.e]);
    if (late) todo.push(['Facturer le solde et clôturer', r.raf]);
    else if (past) todo.push(['Mettre à jour la date de fin dans Flex', null]);
    if (nocond) todo.push(['Affecter un conducteur et valider le budget', null]);
    if (fi) todo.push(['Corriger les données dans Flex', null]);
    if (!todo.length) todo.push(['Rien à signaler', null]);
  } else {
    todo.push(['Marge à terminaison', pcm(tm)]);
    if (r.raf >= 500) todo.push(['Facturer le solde et clôturer', r.raf]);
    else if (r.RC < 1) todo.push(['Clôturer l’affaire', null]);
    if (e === 'r') todo.push(['Retour d’expérience sur le chiffrage', null]);
  }
  return { e, p, d, reason, why: [...new Set(why)].slice(0, 5), todo, fi, nocond, bill, fd, loss };
}

/** État global d'un ensemble de chantiers : critique, à surveiller ou bon. */
export function etatOf(rows: OpsChantier[], prm: OpsParams = OPS_PRM): 'crit' | 'surv' | 'bon' {
  const n = rows.length;
  if (!n) return 'bon';
  const nR = rows.filter((r) => r.c.e === 'r').length;
  const P = rows.reduce((a, r) => addPostes(a, r.Pv), zeroPostes());
  const T = rows.reduce((a, r) => addPostes(a, r.Tm), zeroPostes());
  const dG = (rateOf(T) ?? 0) - (rateOf(P) ?? 0);
  return dG <= -prm.der || nR / n >= 0.2 ? 'crit' : nR > 0 || dG <= -prm.rec ? 'surv' : 'bon';
}

// --- Construction du jeu de données ---

/**
 * Assemble les chantiers à partir des lectures SYNC.
 * `asOf` : date d'arrêté ; `today` : date du jour (ancienneté de la dernière révision du budget).
 */
export function buildOps(raw: OpsRaw, asOf: string, today: string = asOf): OpsDataset {
  const AR: Record<string, { f: number; fy: number; ft: number }> = {};
  for (const x of raw.ar) AR[x.id] = { f: N(x.f), fy: N(x.fy), ft: N(x.ft) };
  const AP: Record<
    string,
    {
      a: number;
      s: number;
      p: number;
      o: number;
      at: number;
      st: number;
      pt: number;
      nc: number;
      ay: number;
      sy: number;
      oy: number;
    }
  > = {};
  for (const x of raw.ap)
    AP[x.id] = {
      a: N(x.a),
      s: N(x.s),
      p: N(x.p),
      o: N(x.o),
      at: N(x.at),
      st: N(x.stt),
      pt: N(x.pt),
      nc: N(x.nc),
      ay: N(x.ay),
      sy: N(x.sy),
      oy: N(x.oy),
    };
  const VT: Record<string, Record<string, number[]>> = {};
  for (const x of raw.vent) {
    const v = Array.from({ length: 12 }, (_, i) => N(x[`m${i + 1}` as `m${number}`]));
    if (v.some((a) => a !== 0)) (VT[x.c] ??= {})[String(x.y)] = v;
  }
  const FA: Record<string, { far: number; fst: number; eng: number }> = {};
  for (const x of raw.far) FA[x.id] = { far: N(x.far), fst: N(x.fst), eng: N(x.eng) };

  const rows: OpsChantier[] = [];
  const internes: OpsInterne[] = [];
  const entsN: Record<string, number> = {};

  for (const x of raw.rev) {
    const code = x.id;
    const ent = S(x.b) ?? '?';
    const cli = S(x.cli);
    const type = S(x.t);
    const deb = S(x.deb);
    const fin = S(x.fin);
    const clo = S(x.clo);
    const pre = !!(deb && deb < '2025-01-01');
    const ca = N(x.ca);
    const car = N(x.car);
    const CAr = car || ca;
    const aP = N(x.ap);
    const aR = N(x.ar);
    const moP = N(x.mop);
    const moR = N(x.mor);
    const moX = N(x.mox);
    const stP = Math.max(N(x.msp) - moP, 0);
    const stR = Math.max(N(x.msr) - moR, 0);
    const mpE = N(x.mp);
    const mrE = N(x.mr);
    const mp = Math.round(N(x.pp) * 10) / 10;
    const mr = Math.round(N(x.pr) * 10) / 10;
    // Autres charges : le reste du budget.
    const oP = ca - aP - stP - moP - mpE;
    const oR = car - aR - stR - moR - mrE;
    const ar = AR[code] ?? { f: 0, fy: 0, ft: 0 };
    const ap = AP[code] ?? { a: 0, s: 0, p: 0, o: 0, at: 0, st: 0, pt: 0, nc: 0, ay: 0, sy: 0, oy: 0 };
    // Chantier démarré avant la réplication comptable : les cumuls partent du bilan d'affaire Flex.
    const fact = pre ? N(x.tf) - (ar.ft - ar.f) : ar.f;
    const achX = pre ? N(x.ax) - (ap.at - ap.a) : ap.a;
    const stX = pre ? Math.max(N(x.msx) - moX, 0) - (ap.st - ap.s) : ap.s;
    const oX = ap.o;
    const cR = aR + stR + moR + Math.max(oR, 0);
    const cX = achX + stX + moX + oX;
    const FR = FA[code] ?? { far: 0, fst: 0, eng: 0 };
    const resteA = Math.max(aR - achX, 0);
    const resteS = Math.max(stR - stX, 0);
    const resteM = Math.max(moR - moX, 0);
    const resteO = Math.max(oR - oX, 0);
    const RC = resteA + resteS + resteM + resteO;
    // Avancement = coûts réalisés ÷ coûts à terminaison (réalisé + reste à dépenser).
    const budC = cX + RC;
    const avC = budC > 0 ? cX / budC : null;
    const cr = avC != null ? Math.round(avC * 100) : 0;
    const fa = CAr > 0 ? Math.round((fact / CAr) * 100) : null;
    const der = mrE - mpE;
    const raf = Math.max(CAr - fact, 0);
    const com = S(x.com);
    const cond = S(x.cond);

    if (['ODICEENT', 'ODICESERV'].includes(ent) || (cli && GROUP.test(cli))) {
      internes.push({
        ent,
        code,
        cli,
        nom: S(x.nom),
        type,
        ca,
        cr: achX + stX + oX,
        kind: OWN[ent] && cli && OWN[ent]!.test(cli) ? 'Interne' : 'Intra-groupe',
      });
      continue;
    }
    entsN[ent] = (entsN[ent] ?? 0) + 1;

    // Actions
    const jr = fin ? daysBetween(asOf, fin) : null;
    const acts: OpsChantier['acts'] = [];
    if (deb && deb >= asOf && deb <= addDays(asOf, 10)) {
      const w: string[] = [];
      if (!cond) w.push('pas de conducteur');
      if (mp < 10) w.push(`marge prévue ${pc1(mp)}`);
      acts.push({
        k: 'start',
        txt: `Valider budget, achats et équipe avant démarrage (marge à sécuriser)${w.length ? ` — ${w.join(', ')}` : ''}`,
        e: Math.max(mpE, 0),
        due: deb,
        w: w.length > 0,
      });
    }
    if (fin && fin >= asOf && fin <= addDays(asOf, 10) && raf > 0)
      acts.push({
        k: 'end',
        txt: `Préparer réception et facture finale (${fullEur(raf)} restant)`,
        e: raf,
        due: fin,
      });
    if (fin && fin < asOf && raf > 0) {
      const n = daysBetween(fin, asOf);
      acts.push({
        k: 'late',
        txt:
          cr >= 90
            ? `Terminé depuis ${n} j : facturer le solde ou clôturer`
            : `Date de fin dépassée de ${n} j : mettre à jour le planning ou facturer`,
        e: raf,
        due: null,
      });
    }
    if (der <= -3000 && mp - mr >= 2)
      acts.push({
        k: 'drift',
        txt: `Marge ${pc1(mp)} → ${pc1(mr)} : chercher avenant ou économies`,
        e: -der,
        due: null,
      });
    if (mrE < 0)
      acts.push({ k: 'loss', txt: `Marge révisée ${pc1(mr)} (${fullEur(mrE)})`, e: -mrE, due: null });
    const over = Math.max(achX - aR, 0) + Math.max(stX - stR, 0) + Math.max(moX - moR, 0);
    if (over > Math.max(5000, 0.05 * cR))
      acts.push({
        k: 'rev',
        txt: `Réalisé supérieur au budget revu de ${fullEur(over)} (achats, sous-traitance ou MO) : réviser le budget`,
        e: over,
        due: null,
      });
    if (aR + N(x.msr) === 0 && CAr > 0)
      acts.push({
        k: 'data',
        txt: 'Budget révisé non saisi : la marge affichée n’est pas fiable',
        e: Math.round(CAr * 0.1),
        due: null,
      });

    // Dates
    const dates: OpsChantier['dates'] = [];
    if (fin && deb && fin < deb)
      dates.push({ k: 'inv', txt: `Fin (${frl(fin)}) avant le début (${frl(deb)})` });
    if (!fin && CAr >= 20000) dates.push({ k: 'nofin', txt: 'Pas de date de fin : planning non renseigné' });
    if (deb && deb < addDays(asOf, -548))
      dates.push({
        k: 'old',
        txt: `Ouvert depuis ${deb.slice(5, 7)}/${deb.slice(0, 4)}, toujours actif : clôturer ou replanifier`,
      });
    if (deb && deb > asOf && cr >= 15)
      dates.push({
        k: 'futstart',
        txt: `Démarrage prévu le ${frl(deb)} mais avancement des coûts déjà à ${cr} %`,
      });
    if (fin && fin >= asOf && fin <= addDays(asOf, 10) && cr < 50)
      dates.push({
        k: 'soon',
        txt: `Fin prévue le ${fr(fin, asOf)} avec un avancement de ${cr} % : fin peu crédible`,
      });
    if (fin && fa != null && fa >= 100 && cr >= 95 && fin > addDays(asOf, 30))
      dates.push({
        k: 'futdone',
        txt: `Facturé à ${fa} % et avancement ${Math.min(cr, 100)} % mais fin prévue le ${frl(fin)} : chantier sans doute terminé`,
      });
    if (fin && fin < asOf && cr < 50 && !(deb && fin < deb))
      dates.push({
        k: 'pastlow',
        txt: `Fin passée depuis ${daysBetween(fin, asOf)} j mais avancement à ${cr} % : chantier pas fini ou date à corriger`,
      });
    if (fin && deb && daysBetween(deb, fin) > 730)
      dates.push({
        k: 'long',
        txt: `Durée prévue de ${Math.round(daysBetween(deb, fin) / 30)} mois : vérifier les dates`,
      });
    const est =
      deb && deb <= asOf && cr >= 5 && cr < 100
        ? addDays(deb, Math.round((daysBetween(deb, asOf) * 100) / cr))
        : null;

    const P = proj({ raf, RC, fin, est }, VT[code], asOf);
    const Pv: Postes = { ca, ach: aP, st: stP, mo: moP, oth: Math.max(oP, 0) };
    const Rv: Postes = { ca: CAr, ach: aR, st: stR, mo: moR, oth: Math.max(oR, 0) };
    const Ad: Postes = { ca: fact, ach: achX, st: stX, mo: moX, oth: oX };
    const Rf: Postes = { ca: Math.max(CAr - fact, 0), ach: resteA, st: resteS, mo: resteM, oth: resteO };
    const Tm: Postes = {
      ca: fact + Rf.ca,
      ach: achX + resteA,
      st: stX + resteS,
      mo: moX + resteM,
      oth: oX + resteO,
    };
    const mPv = margOf(Pv);
    const mTm = margOf(Tm);
    const dT = mTm - mPv;
    const late = acts.find((a) => a.k === 'late');
    const bill = acts.find((a) => a.k === 'bill');
    const h: OpsChantier['h'] =
      mTm < 0 || dT <= -50000 ? 'crit' : dT <= -15000 || late || bill ? 'surv' : 'bon';
    const eac = Math.max(aR, achX) + Math.max(stR, stX) + Math.max(moR, moX) + Math.max(oR, oX);
    const mEst = Math.max(CAr, fact) - eac;

    // Anomalies
    const an: OpsAnomaly[] = [];
    const tol = (v: number) => Math.max(1000, 0.02 * Math.abs(v));
    const tf = N(x.tf);
    const axF = N(x.ax);
    const stF = Math.max(N(x.msx) - moX, 0);
    const lm = S(x.lm);
    if (!pre) {
      if (Math.abs(tf - ar.ft) > tol(tf))
        an.push({
          k: 'flexfact',
          s: 'w',
          txt: `Facturé dans le bilan Flex ${fullEur(tf)}, factures clients comptabilisées ${fullEur(ar.ft)} (écart ${fullEurSigned(tf - ar.ft)})`,
        });
      if (Math.abs(axF - ap.at) > tol(axF))
        an.push({
          k: 'flexach',
          s: 'w',
          txt: `Achats réalisés dans Flex ${fullEur(axF)}, achats 6071/6072 comptabilisés ${fullEur(ap.at)} (écart ${fullEurSigned(axF - ap.at)})`,
        });
      if (Math.abs(stF - (ap.st + ap.pt)) > tol(stF))
        an.push({
          k: 'flexst',
          s: 'w',
          txt: `Sous-traitance réalisée dans Flex ${fullEur(stF)}, comptes 611 comptabilisés ${fullEur(ap.st + ap.pt)} (écart ${fullEurSigned(stF - ap.st - ap.pt)})`,
        });
    }
    if (CAr > 0 && fact > CAr * 1.01 + 100)
      an.push({
        k: 'overfact',
        s: 'c',
        txt: `Facturé ${fullEur(fact)} supérieur au CA revu ${fullEur(CAr)} : avenant non saisi ou facture à vérifier`,
      });
    for (const [l, x2, b] of [
      ['Achats', achX, aR],
      ['Sous-traitance', stX, stR],
      ['MO (Flex)', moX, moR],
    ] as [string, number, number][])
      if (x2 - b > Math.max(500, 0.05 * b))
        an.push({
          k: 'over',
          s: 'c',
          txt: `${l} réalisé ${fullEur(x2)} pour un budget revu de ${fullEur(b)} (dépassement ${fullEur(x2 - b)})`,
        });
    if (aR + N(x.msr) === 0 && CAr > 0)
      an.push({
        k: 'nobudget',
        s: 'c',
        txt: 'Aucun budget révisé (achats, MO, sous-traitance) : la marge révisée n’a pas de sens',
      });
    if (CAr > 0 && (mr < -50 || mr > 80))
      an.push({
        k: 'margeab',
        s: 'c',
        txt: `Marge révisée de ${pc1(mr)} : budget incomplet ou CA mal saisi`,
      });
    if (Math.abs(ap.nc) >= 500)
      an.push({
        k: 'noorder',
        s: 'w',
        txt: `${fullEur(ap.nc)} d’achats ou de sous-traitance facturés sans commande d’achat`,
      });
    if (lm && daysBetween(lm, today) > 120 && (raf > 0 || RC > 0))
      an.push({
        k: 'stale',
        s: 'w',
        txt: `Dernière révision du budget le ${frl(lm)}, il y a ${daysBetween(lm, today)} jours, alors que le chantier n’est pas soldé`,
      });
    if (P.T > 0 && Math.abs(P.T - raf) > Math.max(1000, 0.05 * raf))
      an.push({
        k: 'ventil',
        s: 'w',
        txt: `Planning de facturation Flex ${fullEur(P.T)} pour un reste à facturer de ${fullEur(raf)} (écart ${fullEurSigned(P.T - raf)}) : ventilation à mettre à jour`,
      });
    if (!P.T && raf >= 20000)
      an.push({
        k: 'noventil',
        s: 'w',
        txt: `${fullEur(raf)} restent à facturer sans planning de facturation dans Flex`,
      });
    if (!type) an.push({ k: 'notype', s: 'w', txt: 'Type d’affaire non renseigné dans Flex' });
    if (moX > 0 && moP === 0 && moR === 0)
      an.push({ k: 'monoprev', s: 'w', txt: `${fullEur(moX)} de MO pointée sans aucun budget MO` });
    if (!cond)
      an.push({
        k: 'nocond',
        s: 'i',
        txt: com
          ? `Pas de conducteur : responsable par défaut ${com} (commercial)`
          : 'Ni conducteur ni commercial renseigné',
      });

    const row: OpsChantier = {
      ent,
      code,
      nom: S(x.nom),
      cli,
      type,
      com,
      cond,
      resp: cond || com || null,
      ca,
      car,
      CAr,
      fact,
      raf,
      fa,
      cr,
      mp,
      mr,
      mpE,
      mrE,
      der,
      deb,
      fin,
      clo,
      closedAfter: S(x.s) !== 'Actif',
      lm,
      hist: pre ? 'flex' : 'compta',
      achX,
      stX,
      moX,
      oX,
      cX,
      cR,
      aP,
      aR,
      stP,
      stR,
      moP,
      moR,
      oP,
      oR,
      resteA,
      resteS,
      resteM,
      resteO,
      RC,
      av: avC,
      far: FR.far,
      eng: FR.eng,
      budC,
      realC: cX,
      mEst,
      est,
      jr,
      acts,
      dates,
      anoms: an,
      Pv,
      Rv,
      Ad,
      Rf,
      Tm,
      dT,
      h,
      enjS: 0,
      P,
      c: undefined as unknown as OpsClass,
    };
    row.c = classify(row, asOf);
    row.enjS = Math.max(-dT, 0, row.c.fd && raf >= 500 ? raf : 0, row.c.bill ? row.c.bill.e : 0);
    rows.push(row);
  }

  const ents: Record<string, { n: number }> = {};
  for (const e of Object.keys(entsN)) ents[e] = { n: entsN[e]! };
  const mNext = asOf.slice(0, 7);
  const n1: OpsN1Order[] = [];
  for (const x of raw.n1 ?? []) {
    const m = ymAdd(x.m, 12);
    const ent = S(x.b) ?? '?';
    const cli = S(x.cli);
    const code = S(x.c);
    const v = N(x.v);
    if (Math.abs(v) < 1 || ['ODICEENT', 'ODICESERV'].includes(ent) || (cli && GROUP.test(cli))) continue;
    n1.push({
      code: code === '-' ? null : code,
      ent,
      type: S(x.t),
      cli,
      com: S(x.com),
      ds: S(x.ds),
      m0: x.m,
      m: m < mNext ? mNext : m,
      v,
      n: N(x.n),
    });
  }
  return { asOf, rows, internes, ents, n1 };
}
